import React from 'react';
import { array, func, node, string } from 'prop-types';
import classNames from 'classnames';
import { FormattedMessage } from '../../util/reactIntl';
import { FiPaperclip } from 'react-icons/fi';
import {
  FieldTextInput,
  StagedAttachmentChips,
  AttachmentHints,
  useAttachmentFilePicker,
  useUnsavedAttachmentsWarning,
  keepComposerFocus,
} from '../../components';
import { MAX_MESSAGE_ATTACHMENTS } from '../../util/fileAttachments';

import css from './FieldMessageComposer.css';

const noop = () => null;

// Message field per the "Enquiry Modal" design: a label, a bordered box holding
// the textarea and an in-field toolbar with the attach control, the staged
// attachment chips and a character count, plus upload hints below. Must render
// inside a Final Form. Attachments are opt-in: the attach control shows only
// when both staging handlers are given.
const FieldMessageComposer = props => {
  const {
    rootClassName,
    className,
    textareaClassName,
    id,
    name,
    label,
    placeholder,
    message,
    stagedAttachments,
    onUploadAttachment,
    onRemoveAttachment,
  } = props;

  const showAttachments = !!onUploadAttachment && !!onRemoveAttachment;
  const attachments = stagedAttachments || [];

  const {
    fileInputRef,
    pickErrorId,
    clearPickError,
    handleFilesSelected,
    openFilePicker,
  } = useAttachmentFilePicker({
    stagedAttachments: attachments,
    onUploadAttachment: onUploadAttachment || noop,
  });
  useUnsavedAttachmentsWarning(attachments.length > 0);

  const handleRemove = tempId => {
    clearPickError();
    onRemoveAttachment(tempId);
  };

  const trimmedMessage = (message || '').trim();
  // The strip under the text holds the attach control and the character
  // count; without either there is nothing to show.
  const showToolbar = showAttachments || !!trimmedMessage;
  const attachDisabled = attachments.length >= MAX_MESSAGE_ATTACHMENTS;

  return (
    <div className={classNames(rootClassName || css.root, className)}>
      {label ? (
        <label htmlFor={id} className={css.label}>
          {label}
        </label>
      ) : null}
      <div className={css.fieldBox}>
        <FieldTextInput
          className={css.field}
          inputRootClass={classNames(css.textarea, textareaClassName)}
          type="textarea"
          name={name}
          id={id}
          placeholder={placeholder}
          showSpacer={false}
        />
        {showToolbar ? (
          <div className={css.toolbar}>
            {showAttachments ? (
              <>
                <button
                  type="button"
                  className={css.attachButton}
                  onMouseDown={keepComposerFocus}
                  onClick={openFilePicker}
                  disabled={attachDisabled}
                >
                  <FiPaperclip className={css.attachIcon} />
                  <FormattedMessage id="FieldMessageComposer.attachFile" />
                </button>
                <input
                  ref={fileInputRef}
                  className={css.fileInput}
                  type="file"
                  multiple
                  onChange={handleFilesSelected}
                />
                {attachments.length > 0 ? (
                  <StagedAttachmentChips
                    className={css.chips}
                    attachments={attachments}
                    onRemove={handleRemove}
                  />
                ) : null}
              </>
            ) : null}
            {trimmedMessage ? (
              <span className={css.count}>
                <FormattedMessage
                  id="FieldMessageComposer.characterCount"
                  values={{ count: trimmedMessage.length }}
                />
              </span>
            ) : null}
          </div>
        ) : null}
      </div>
      {showAttachments ? (
        <AttachmentHints attachments={attachments} pickErrorId={pickErrorId} />
      ) : null}
    </div>
  );
};

FieldMessageComposer.defaultProps = {
  rootClassName: null,
  className: null,
  textareaClassName: null,
  label: null,
  placeholder: null,
  message: '',
  stagedAttachments: [],
  onUploadAttachment: null,
  onRemoveAttachment: null,
};

FieldMessageComposer.propTypes = {
  rootClassName: string,
  className: string,
  // Appended to the textarea, e.g. to change its minimum height.
  textareaClassName: string,
  id: string.isRequired,
  name: string.isRequired,
  label: node,
  placeholder: string,
  // Current field value, for the character count.
  message: string,

  stagedAttachments: array,
  onUploadAttachment: func,
  onRemoveAttachment: func,
};

export default FieldMessageComposer;
